import { ArrowUpRight, Trophy, Waves, Mountain, Target } from "lucide-react";
import { Link } from "react-router-dom";
import { ORIG } from "../mock";
import Reveal from "./Reveal";
import CountUp from "./CountUp";

const sportsCards = [
  {
    title: "Cricket",
    italic: "on a champion's pitch",
    body: "Olympic-standard field with practice nets, professional coaching and floodlit play.",
    image: `${ORIG}/banner-sport.jpg`,
    Icon: Trophy,
  },
  {
    title: "Swimming",
    italic: "year-round, all weather",
    body: "A heated semi-Olympic pool with structured programmes from learner to competitive.",
    image: `${ORIG}/banner-sport1.jpg`,
    Icon: Waves,
  },
  {
    title: "Equestrian",
    italic: "poise on horseback",
    body: "Confidence, posture and discipline — taught by certified riding instructors.",
    image: `${ORIG}/banner-sport2.jpg`,
    Icon: Mountain,
  },
];

const sportsStats = [
  { value: "60K", label: "Sq.Ft. Indoor Complex" },
  { value: "15+", label: "Sporting Disciplines" },
  { value: "1", label: "Olympic-Std Cricket Field" },
  { value: "All", label: "Weather Play" },
];

export default function SportsHome() {
  return (
    <section id="sports" className="relative bg-[var(--cream)] py-12 md:py-20 overflow-hidden">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10">
        {/* Heading */}
        <Reveal>
          <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-14">
            <div className="eyebrow mb-5">Beyond Academics</div>
            <h2 className="h-section text-[var(--ink)]">
              Sport,
              <span className="serif-italic gold-text"> the quiet teacher.</span>
            </h2>
            <span className="gold-divider mx-auto" />
            <p className="text-[15px] md:text-[16.5px] leading-[1.85] text-[var(--ink)]/75">
              Discipline, resilience and the joy of effort — built every day on our fields, pitches and pool. World-class facilities give every child the room to find the sport they love.
            </p>
          </div>
        </Reveal>

        {/* 3-card grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
          {sportsCards.map((c, i) => {
            const Icon = c.Icon;
            return (
              <Reveal key={c.title} variant="zoom" delay={i * 120}>
                <article className="group relative overflow-hidden light-card">
                  <div className="relative aspect-[4/5] overflow-hidden bg-[var(--navy)]">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(6,13,34,0.92)] via-[rgba(6,13,34,0.25)] to-transparent" />

                    {/* number */}
                    <div className="absolute top-5 left-5 serif-italic text-[var(--gold-light)] text-[14px] tracking-[0.18em]">
                      0{i + 1}
                    </div>
                    {/* icon */}
                    <div className="absolute top-5 right-5 w-10 h-10 rounded-full border border-[var(--gold)] flex items-center justify-center text-[var(--gold)] bg-[rgba(6,13,34,0.4)] backdrop-blur-sm transition-colors group-hover:bg-[var(--gold)] group-hover:text-[var(--navy-deep)]">
                      <Icon size={16} strokeWidth={1.5} />
                    </div>

                    {/* Content overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                      <h3 className="serif text-white text-[26px] md:text-[32px] leading-tight">
                        {c.title}
                        <span className="block serif-italic gold-text text-[16px] md:text-[18px] mt-0.5">{c.italic}</span>
                      </h3>
                      <span className="gold-divider-thin block my-3" />
                      <p className="text-[13.5px] md:text-[14.5px] leading-[1.7] text-white/80">{c.body}</p>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        {/* Stats strip */}
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
          {sportsStats.map((s, i) => (
            <Reveal key={s.label} delay={i * 100}>
              <div className="flex flex-col items-center text-center">
                <div className="stat-number">
                  <CountUp value={s.value} />
                </div>
                <span className="gold-divider-thin my-3" />
                <div className="text-[11px] md:text-[12px] tracking-[0.22em] uppercase text-[var(--muted)] max-w-[180px]">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA */}
        <Reveal>
          <div className="mt-12 md:mt-14 text-center">
            <Link
              to="/sports.html"
              className="btn-gold inline-flex items-center gap-2"
              style={{ background: "var(--gold)", color: "var(--navy-deep)", borderColor: "var(--gold)" }}
            >
              Explore Sports <ArrowUpRight size={18} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
